import { InjectionKey } from 'vue';
import { createStore, Store, useStore as baseUseStore } from 'vuex';
import types from './types';
import { member } from '@/types/member';
import { rule } from '@/types/rule';
import { teamInfo } from '@/types/teamInfo';
import { myTeamRankInfo } from '@/types/myTeamRankInfo';
import { shareExtendInfo } from '@/types/shareExtendInfo';
import { record } from '@/types/record';

// 为 store state 声明类型

export interface State {
  user: {
    uid: number;
    name: string;
    avatar: string;
  };
  /**
   * 队长uid
   */
  teamLeaderUid: number;
  title: string;
  /**
   * 是否分享页面
   */
  isShare: boolean;
  /**
   * 分享追踪
   */
  shareCode: string;
  currentMembers: Array<member>;
  /**
   * 是否已经加入当前队伍
   */
  joinedTeam: boolean;
  rule: rule;
  myTeamRank: myTeamRankInfo;
  teamRanks: Array<teamInfo>;
  avatarHost: string;
  bannerForNoTeam: string;
  bannerForTeam: string;
  period: string;
  shareExtendInfo: shareExtendInfo;
  shareLink: string;
  records: Array<record>;
  teamMinObtain: number;
  previousPeriodObtainFlag: boolean;
  previousPeriodObtainCount: number;
  hasShowPrizeDialog: boolean;
  pageDataReady: boolean;
  randkDataReady: boolean;
  pvTracked: boolean;
  host: string;
  showNoRecord: boolean;
  showJoin: boolean;
  activityTab: number;
  blackListTeam: boolean;
}

export const key: InjectionKey<Store<State>> = Symbol();

export function useStore () {
  return baseUseStore(key);
}

export const store = createStore<State>({
  state: {
    user: {
      uid: 0,
      name: '',
      avatar: '',
    },
    teamLeaderUid: -1,
    title: '',
    isShare: false,
    shareCode: '',
    currentMembers: [],
    joinedTeam: false,
    bannerForNoTeam: '',
    bannerForTeam: '',
    period: '',
    rule: {
      teamMaxPerson: 0,
      inviteNewUserCanObtain: 0,
      inviteOldUserCanObtain: 0,
      joinShareAssistanceTeamCanObtain: 0,
      maxKkbCanObtain: 0,
      clockOnCanObtain: 0,
      description: '',
    },
    myTeamRank: {
      rank: 0,
      total: 0,
      leaderImg: '',
      name: '',
      totalInfo: '',
    },
    teamRanks: [],
    avatarHost: '',
    shareExtendInfo: {
      inviteUserAvatar: '',
      inviteUserId: 0,
      inviteUserName: '',
      joinedTeam: false,
    },
    shareLink: '',
    records: [],
    teamMinObtain: 0,
    previousPeriodObtainFlag: false,
    previousPeriodObtainCount: 0,
    hasShowPrizeDialog: false,
    pageDataReady: false,
    randkDataReady: false,
    pvTracked: false,
    host: '',
    showNoRecord: false,
    showJoin: false,
    activityTab: 0,
    blackListTeam: false,
  },
  getters: {
    /**
     * 页面PV所需要的数据都准备好了
     * @param state
     * @returns
     */
    pvDataReady (state) {
      return state.pageDataReady && state.randkDataReady;
    },
    fixedBtn (state) {
      const activeTab = state.activityTab;
      const membersLength = state.currentMembers.length;
      const ranksLength = state.teamRanks.length;
      if (activeTab === 0) {
        return membersLength >= 5;
      } else {
        return ranksLength >= 5;
      }
    },
  },
  mutations: {
    [types.LOGIN] (state, user) {
      Object.assign(state.user, user);
    },
    [types.SET_TITLE] (state, title: string) {
      state.title = title;
    },
    [types.SET_SHARE_CODE] (state, shareCode: string) {
      state.isShare = true;
      state.shareCode = shareCode;
    },
    [types.SET_CURRENT_MEMBER] (state, members: Array<member>) {
      state.currentMembers = members;
    },
    [types.SET_JOINED_TEAM] (state, joinedTeam: boolean) {
      state.joinedTeam = joinedTeam;
      state.isShare = !joinedTeam;
    },
    [types.SET_ASSIST_RULE] (state, rule: rule) {
      state.rule = rule;
    },
    [types.SET_TEAM_LEADER] (state, id: number) {
      state.teamLeaderUid = id;
    },
    [types.SET_MY_TEAM_RANK] (state, rank: myTeamRankInfo) {
      state.myTeamRank = rank;
    },
    [types.SET_TEAM_RANK] (
      state,
      payload: {
        list: Array<teamInfo>;
        host: string;
      }
    ) {
      state.teamRanks = payload.list;
      state.avatarHost = payload.host;
    },
    [types.SET_HEADER_INFO] (state, payload) {
      state.bannerForNoTeam = payload.bannerForNoTeam;
      state.bannerForTeam = payload.bannerForTeam;
      state.period = payload.period;
    },
    [types.SET_SHARE_EXTRA_INFO] (state, info: shareExtendInfo) {
      state.shareExtendInfo = info;
    },
    [types.SET_SHARE_LINK] (state, link: string) {
      state.shareLink = link;
    },
    [types.SET_RECORDS] (state, records: Array<record>) {
      state.records = records;
    },
    [types.SET_TEAM_MIN_OBTAIN] (state, num: number) {
      state.teamMinObtain = num;
    },
    [types.SET_PREVIEW_PRIZE_STATUS] (state, status: boolean) {
      state.previousPeriodObtainFlag = status;
    },
    [types.SET_HAS_PRIZE_DIALOG_OPEN] (state, flag: boolean) {
      state.hasShowPrizeDialog = flag;
    },
    [types.SET_PREVIEW_PRIZE_NUMBER] (state, num: number) {
      state.previousPeriodObtainCount = num;
    },
    [types.SET_PAGE_DATA_READY] (state) {
      state.pageDataReady = true;
    },
    [types.SET_RANK_DATA_READY] (state) {
      state.randkDataReady = true;
    },
    [types.SET_PV_TRACKED] (state) {
      state.pvTracked = true;
    },
    [types.SET_HOST] (state, host: string) {
      state.host = host;
    },
    [types.SET_SHOW_NO_RECORD] (state, flag: boolean) {
      state.showNoRecord = flag;
    },
    [types.SET_SHOW_JOIN] (state, flag: boolean) {
      state.showJoin = flag;
    },
    [types.SET_CURRENT_TAB] (state, active: number) {
      state.activityTab = active;
    },
    [types.SET_BLACKLISTTEAM] (state, flag: boolean) {
      state.blackListTeam = flag;
    },
  },
  actions: {},
  modules: {},
});
